import { useDialog } from 'muibox';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

// A single ordered live-stream tip goal with its own independent auto-kick settings.
export interface LiveStreamGoal {
    id?: string;
    onlyfansId: number;
    position: number;
    tipsGoal: string;
    tipsGoalSum: number;
    autoKickEnabled: boolean;
    tippedHoursAgo: number;
    usersCanWatchMinutes: number;
    usersCanWatchMinutesOnRejoin: number;
    numberOfRejoinsAllowed: number;
    publicShameFan: boolean;
    sendWarningMessages: boolean;
    onlySendFinalWarning: boolean;
    whiteListListId: string;
    minimumTipAmount: number;
}

const useSextForceLiveStreamGoals = () => {
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const userContext = useContext(UserContext);

    const getGoals = async (): Promise<LiveStreamGoal[]> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals`;

        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            });
        return data;
    };

    const createGoal = async (payload: LiveStreamGoal): Promise<LiveStreamGoal | null> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals/create`;
        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return null;
            });
        return data;
    };

    const updateGoal = async (payload: LiveStreamGoal): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals/update`;
        const data = await axios
            .post(query, payload)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const deleteGoal = async (id: string): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals/delete`;
        const data = await axios
            .post(query, { id })
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const reorderGoals = async (onlyfansId: number, orderedIds: string[]): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals/reorder`;
        const data = await axios
            .post(query, { onlyfansId, orderedIds })
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const setActiveGoal = async (index: number): Promise<{ success: boolean }> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals/setActive/${index}`;
        const data = await axios
            .post(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return { success: false };
            });
        return data;
    };

    const getServiceStatus = async (): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals/service`;
        const data = await axios
            .get(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const setServiceStatus = async (status: boolean): Promise<boolean> => {
        const query: string = `${settingsContext.routes.liveStream.base}${params.userId}/goals/service/${status}`;
        const data = await axios
            .post(query)
            .then(async response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return false;
            });
        return data;
    };

    const theGoals = useQuery(
        ['liveStreamGoals', params.userId],
        () => {
            return getGoals();
        },
        {
            staleTime: 60 * 1000 * 15, // 15 minutes
            refetchOnWindowFocus: false,
            enabled: !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
        },
    );

    return {
        ...theGoals,
        createGoal,
        updateGoal,
        deleteGoal,
        reorderGoals,
        setActiveGoal,
        getServiceStatus,
        setServiceStatus,
    };
};

export default useSextForceLiveStreamGoals;
